<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PipelineAPI from '../../../../../../api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'update:checklistItems', 'goToTab']);

const { t } = useI18n();

const newChecklistItem = ref('');
const hideCompletedItems = ref(false);
const editingChecklistItemId = ref(null);
const editingChecklistItemText = ref('');

const filteredChecklistItems = computed(() => {
  if (hideCompletedItems.value) {
    return props.checklistItems.filter(i => !i.completed);
  }
  return props.checklistItems;
});

const totalItems = computed(() => props.checklistItems.length);
const completedItems = computed(
  () => props.checklistItems.filter(i => i.completed).length
);

const checklistProgress = computed(() => {
  if (totalItems.value === 0) return 0;
  return (completedItems.value / totalItems.value) * 100;
});

const progressBarGradient = computed(() => {
  const pct = checklistProgress.value;
  if (pct < 33) return 'bg-gradient-to-r from-red-400 to-red-500';
  if (pct < 66) return 'bg-gradient-to-r from-amber-400 to-amber-500';
  if (pct < 100) return 'bg-gradient-to-r from-blue-400 to-blue-500';
  return 'bg-gradient-to-r from-green-400 to-green-500';
});

const addChecklistItem = async () => {
  if (!newChecklistItem.value.trim()) return;

  try {
    const newItem = {
      id: Date.now(),
      text: newChecklistItem.value.trim(),
      completed: false,
      created_at: new Date().toISOString(),
    };

    const updatedChecklist = [...props.checklistItems, newItem];

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    const { data } = await PipelineAPI.updateCard(props.item.id, payload);
    emit('update:checklistItems', data.item_details.checklist);
    emit('itemUpdated');

    newChecklistItem.value = '';

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CHECKLIST_ITEM_ADDED'),
      action: { type: 'success' },
    });

    setTimeout(() => {
      emit('goToTab', 'checklist');
    }, 100);
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CHECKLIST_ITEM_ERROR'),
      action: { type: 'error' },
    });
  }
};

const toggleChecklistItem = async item => {
  try {
    const updatedChecklist = props.checklistItems.map(i => ({
      ...i,
      completed: i.id === item.id ? !i.completed : i.completed,
    }));

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    const { data } = await PipelineAPI.updateCard(props.item.id, payload);
    emit('update:checklistItems', data.item_details.checklist);

    setTimeout(() => {
      emit('goToTab', 'checklist');
    }, 100);
  } catch (error) {
    // Error handled silently
  }
};

const removeChecklistItem = async itemToRemove => {
  try {
    const updatedChecklist = props.checklistItems.filter(
      i => i.id !== itemToRemove.id
    );

    emit('update:checklistItems', updatedChecklist);

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    await PipelineAPI.updateCard(props.item.id, payload);

    setTimeout(() => {
      emit('goToTab', 'checklist');
    }, 100);
  } catch (error) {
    emit('update:checklistItems', props.item.item_details?.checklist || []);
  }
};

const startEditChecklistItem = item => {
  editingChecklistItemId.value = item.id;
  editingChecklistItemText.value = item.text;
  newChecklistItem.value = item.text;
};

const saveEditedChecklistItem = async () => {
  try {
    const updatedChecklist = props.checklistItems.map(item => {
      if (item.id === editingChecklistItemId.value) {
        return { ...item, text: newChecklistItem.value };
      }
      return item;
    });

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    await PipelineAPI.updateCard(props.item.id, payload);
    emit('update:checklistItems', updatedChecklist);

    editingChecklistItemId.value = null;
    editingChecklistItemText.value = '';
    newChecklistItem.value = '';

    setTimeout(() => {
      emit('goToTab', 'checklist');
    }, 100);
  } catch (error) {
    // Error handled silently
  }
};

const cancelEditChecklistItem = () => {
  editingChecklistItemId.value = null;
  editingChecklistItemText.value = '';
  newChecklistItem.value = '';
};
</script>

<template>
  <div class="space-y-4">
    <!-- Progress Section -->
    <div v-if="totalItems > 0" class="space-y-2 mb-4">
      <div class="flex items-center justify-between">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ t('PIPELINE.FORM.CHECKLIST.PROGRESS') }}
        </h4>
        <span class="text-xs font-semibold text-n-slate-12">
          {{ completedItems }}/{{ totalItems }}
        </span>
      </div>

      <div class="h-2 bg-n-solid-3 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-500"
          :class="progressBarGradient"
          :style="{ width: `${checklistProgress}%` }"
        />
      </div>
    </div>

    <!-- Checklist Title -->
    <h3 class="text-heading-3 text-n-slate-12">
      {{ t('PIPELINE.CHECKLIST_LABEL') }}
    </h3>

    <!-- Add new item -->
    <div class="flex gap-2 mb-4">
      <input
        v-model="newChecklistItem"
        type="text"
        class="flex-1 px-3 py-2 text-sm border border-n-weak bg-n-solid-3 rounded-lg focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand text-n-slate-12 placeholder-n-slate-9"
        :placeholder="t('PIPELINE.CHECKLIST_PLACEHOLDER')"
        @keyup.enter="addChecklistItem"
      />
      <button
        class="px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:brightness-110 disabled:opacity-50"
        :disabled="!newChecklistItem.trim()"
        @click="addChecklistItem"
      >
        {{ t('PIPELINE.ADD') }}
      </button>
    </div>

    <!-- Checklist items -->
    <div class="space-y-2">
      <div
        v-for="checkItem in filteredChecklistItems"
        :key="checkItem.id"
        class="flex items-center gap-3 p-3 bg-n-solid-3 rounded-lg"
      >
        <input
          type="checkbox"
          :checked="checkItem.completed"
          class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
          @change="toggleChecklistItem(checkItem)"
        />
        <!-- Edit mode -->
        <template v-if="editingChecklistItemId === checkItem.id">
          <input
            v-model="newChecklistItem"
            type="text"
            class="flex-1 px-2 py-1 text-sm border border-n-weak bg-n-solid-2 rounded focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand text-n-slate-12"
            @keyup.enter="saveEditedChecklistItem"
            @keyup.escape="cancelEditChecklistItem"
          />
          <button
            class="p-1 rounded hover:bg-teal-3 text-teal-11"
            @click="saveEditedChecklistItem"
          >
            <fluent-icon icon="checkmark" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10"
            @click="cancelEditChecklistItem"
          >
            <fluent-icon icon="dismiss" size="14" />
          </button>
        </template>
        <!-- View mode -->
        <template v-else>
          <span
            class="flex-1 text-sm"
            :class="
              checkItem.completed
                ? 'text-n-slate-9 line-through'
                : 'text-n-slate-12'
            "
          >
            {{ checkItem.text }}
          </span>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
            :title="t('PIPELINE.EDIT')"
            @click="startEditChecklistItem(checkItem)"
          >
            <fluent-icon icon="edit" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-ruby-3 text-n-slate-10 hover:text-ruby-11"
            :title="t('PIPELINE.DELETE')"
            @click="removeChecklistItem(checkItem)"
          >
            <fluent-icon icon="delete" size="14" />
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
